// Site-level settings for the Futures Allocation report.
//
// There is no report type for the dashboard, and adding one means a backend change, so
// the settings ride along inside the site's Position report configuration. The backend
// stores that configuration as an opaque JSON string and the dashboard already parses
// it, which makes it the one place both the dashboard and the settings pages can reach
// without new endpoints. Everything here is kept under a single key so the Position
// report's own settings are never touched.

export const FUTURES_ALLOCATION_SETTINGS_KEY = 'futuresAllocation';

export interface FuturesAllocationSettings {
    /**
     * Child locations that trade their own futures and swaps instead of taking them
     * from the parent through an exchange for physical. Their hedges are counted in
     * the parent location's table rather than their own card.
     */
    locationIdsWithoutEfp: number[];
}

export const EMPTY_FUTURES_ALLOCATION_SETTINGS: FuturesAllocationSettings = { locationIdsWithoutEfp: [] };

function parseConfiguration(configurationJson: string | null | undefined): Record<string, unknown> {
    if (!configurationJson) return {};
    try {
        const parsed = JSON.parse(configurationJson);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        return {};
    }
}

/** The report settings held in a Position report configuration, or empty settings when none are stored. */
export function readFuturesAllocationSettings(configurationJson: string | null | undefined): FuturesAllocationSettings {
    const raw = parseConfiguration(configurationJson)[FUTURES_ALLOCATION_SETTINGS_KEY] as Partial<FuturesAllocationSettings> | undefined;
    const ids = Array.isArray(raw?.locationIdsWithoutEfp) ? raw!.locationIdsWithoutEfp : [];

    return {
        locationIdsWithoutEfp: Array.from(new Set(ids.filter((id): id is number => typeof id === 'number' && Number.isFinite(id)))).sort((a, b) => a - b),
    };
}

/** The configuration JSON with the report settings replaced and every other key left as it was. */
export function writeFuturesAllocationSettings(configurationJson: string | null | undefined, settings: FuturesAllocationSettings): string {
    const configuration = parseConfiguration(configurationJson);
    const locationIdsWithoutEfp = Array.from(new Set(settings.locationIdsWithoutEfp)).sort((a, b) => a - b);

    return JSON.stringify({
        ...configuration,
        [FUTURES_ALLOCATION_SETTINGS_KEY]: { locationIdsWithoutEfp },
    });
}
